import { keys } from '@/utils'

import { ApplicationConfigService } from './applicationConfig.service'

export type Font = {
  id: string | null
  name: string
  postScriptName: string
  previewUrl?: string
}
type FallbackFont = {
  publicResourceName: string
  fontName: string
}

export type FontSet = Record<string, Font | undefined | null>
export type FallbackFontSet<Set extends FontSet> = Record<
  keyof Set,
  FallbackFont
>

export type PreviewFontSet = {
  fontTitle?: Font | null
  fontTextRegular?: Font | null
  fontTextRegularItalic?: Font | null
  fontTextSemiBold?: Font | null
  fontTextBold?: Font | null
  fontTextBoldItalic?: Font | null
}
export const MobilePreviewFallbackFonts: FallbackFontSet<PreviewFontSet> = {
  fontTextBold: {
    publicResourceName: 'font-default-text-bold.ttf',
    fontName: 'Raleway-Bold',
  },
  fontTextBoldItalic: {
    publicResourceName: 'font-default-text-bold-italic.ttf',
    fontName: 'Raleway-BoldItalic',
  },
  fontTextRegular: {
    publicResourceName: 'font-default-text-regular.ttf',
    fontName: 'Raleway-Regular',
  },
  fontTextRegularItalic: {
    publicResourceName: 'font-default-text-regular-italic.ttf',
    fontName: 'Raleway-Italic',
  },
  fontTextSemiBold: {
    publicResourceName: 'font-default-text-semibold.ttf',
    fontName: 'Raleway-SemiBold',
  },
  fontTitle: {
    publicResourceName: 'font-default-title.ttf',
    fontName: 'BebasNeue-Regular',
  },
}

type StaticPreviewFontSet = {
  fontText: Font | undefined
  fontTitle: Font | undefined
}
const StaticPreviewFallbackFonts: FallbackFontSet<StaticPreviewFontSet> = {
  fontText: {
    publicResourceName: 'font-default-text-regular.ttf',
    fontName: 'ChapitoText',
  }, // these two names are hard-coded in the static site
  fontTitle: {
    publicResourceName: 'font-default-title.ttf',
    fontName: 'ChapitoTitle',
  },
}

const state: {
  styleElement: HTMLStyleElement | undefined
} = {
  styleElement: undefined,
}

export const FontsService = {
  MobilePreviewFallbackFonts,
  StaticPreviewFallbackFonts,

  async createPreviewStylesFromAppConfig(): Promise<void> {
    const {
      fontTitle,
      fontTextBold,
      fontTextBoldItalic,
      fontTextRegular,
      fontTextRegularItalic,
      fontTextSemiBold,
    }: PreviewFontSet = await ApplicationConfigService.get()
    const allFonts = {
      fontTitle,
      fontTextBold,
      fontTextBoldItalic,
      fontTextRegular,
      fontTextRegularItalic,
      fontTextSemiBold,
    }
    this.createStylesForFontSet(allFonts, this.MobilePreviewFallbackFonts)
  },

  createStylesForFontSet<Fonts extends FontSet>(
    fonts: Fonts,
    fallbacks: FallbackFontSet<Fonts>,
  ): void {
    if (!state.styleElement) {
      state.styleElement = document.createElement('style')
      document.head.appendChild(state.styleElement)
    }
    state.styleElement.innerHTML = keys(fonts)
      .map((fontName) => {
        const font = fonts[fontName]
        const fallback = fallbacks[fontName]
        const source = font?.id ?? font?.previewUrl
        if (font && source != null && source !== '') {
          const url = font.previewUrl ?? `/api/files/${font.id}`
          return `@font-face { font-family: '${font.postScriptName}'; src: url('${url}') format('truetype'); font-display: swap; }\n`
        }
        return `@font-face { font-family: '${fallback.fontName}'; src: url('/api/public-files/${fallback.publicResourceName}') format('truetype') }\n`
      })
      .join('')
  },
}
